import {afterNextRender, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {trigger, style, transition, animate, AnimationEvent} from '@angular/animations';
import {MegaMenuItem, MenuItem} from 'primeng/api';
import {AppComponent} from "../../app.component";
import {SiteLayoutComponent} from "../../core/layout/site/site-layout.component";
import {CurrentUserViewModel} from "../../core/models/current-user.view-model";
import {TokenDataProviderService} from "../../core/services/token-data-provider.service";
import {TranslateService} from "@ngx-translate/core";
import {filter, tap} from "rxjs/operators";
import {Router} from "@angular/router";
import {NotificationInterface} from "../interfaces/notification.interface";
import {NotificationDataProviderService} from "../../core/services/notification.data-provider.service";
import {ResultListApiModel} from "../../api/models/result-list.api.model";
import {EventSourceInterface} from "../../core/interfaces/event-source.interface";
import {AbstractComponent} from "../../core/components/abstract-component";
import {ChatDataProviderService} from "../../features/chat/chat.data-provider.service";
import {EventInvitationRestService} from "../../api/services/event-invitation.rest.service";
import {EventInvitationApiModel} from "../../api/models/event-invitation.api-model";
import {OwnerInvitationRestService} from "../../api/services/owner-invitation.rest.service";
import {OwnerInvitationApiModel} from "../../api/models/owner-invitation.api-model";
import {EventParticipationRequestRestService} from "../../api/services/event-participation-request.rest.service";
import {ResponseNotificationService} from "../../core/services/response-notification.service";
import {HttpErrorResponse} from "@angular/common/http";
import {MediaObjectOptionsInterface} from "../../core/pipes/media-object.pipe";
import {TutorialOrchestratorService} from "../../core/onboarding/tutorial-orchestrator.service";
import {getMainNavigationItems, INSPECTION_NAVIGATION_ITEMS} from '../navigation/main-navigation.config';
import {MegaMenu} from 'primeng/megamenu';


@Component({
  selector: 'app-topbar',
  templateUrl: './app.topbar.component.html',
  styleUrls: ['./app.topbar.component.scss'],
  animations: [
    trigger('topbarActionPanelAnimation', [
      transition(':enter', [
        style({opacity: 0, transform: 'scaleY(0.8)'}),
        animate('.12s cubic-bezier(0, 0, 0.2, 1)', style({opacity: 1, transform: '*'})),
      ]),
      transition(':leave', [
        animate('.1s linear', style({opacity: 0}))
      ])
    ])
  ]
})
export class AppTopBarComponent extends AbstractComponent implements OnInit, OnDestroy {
  readonly inspectionNavigationItems = INSPECTION_NAVIGATION_ITEMS;
  private readonly mainNavigationItems = getMainNavigationItems().filter(item => item.showInTopbar);
  public compactNavigation = true;
  public desktopNavigation = false;
  public showNavigationIcons = false;
  public compactNavigationModel: MegaMenuItem[] = [];
  @ViewChild('navigationSlot') private navigationSlot?: ElementRef<HTMLElement>;
  @ViewChild('fullNavigation') private fullNavigation?: MegaMenu;
  @ViewChild('fullNavigation', {read: ElementRef}) private fullNavigationElement?: ElementRef<HTMLElement>;
  @ViewChild('compactMenu') private compactMenu?: MegaMenu;
  private navigationResizeObserver?: ResizeObserver;
  private desktopNavigationQuery?: MediaQueryList;
  private navigationFrameId: number | null = null;
  private navigationInitialized = false;
  private navigationDestroyed = false;
  private readonly onNavigationResize = (): void => this.scheduleNavigationMeasurement();
  private readonly notificationPlaceholderRowHeight = 78;
  get notifications(): NotificationInterface[] {
    return this._notifications;
  }

  set notifications(value: NotificationInterface[]) {
    this._notifications = value;
  }

  get notificationTotalItems(): number {
    return this.notificationDataProvider.notifications.total;
  }

  get newNotificationsCount(): number {
    return this.unreadNotificationCount;
  }

  get newNotifications(): NotificationInterface[] {
    return this.notifications.filter((e: NotificationInterface) => e.readAt === null)
  }

  get model(): MegaMenuItem[] {
    return this._model;
  }

  set model(value: MegaMenuItem[]) {
    this._model = value;
  }

  get user(): CurrentUserViewModel {
    return this._user;
  }

  @Input() set user(value: CurrentUserViewModel) {
    this._user = value;
    this.maybeLoadNotifications();
  }

  private _user: CurrentUserViewModel;

  private _notifications: NotificationInterface[] = [];
  public pageRows: number = 20;
  private readonly maxRenderedNotifications = 120;
  public currentPage: number = 1;
  public unreadChatCount = 0;
  public unreadNotificationCount = 0;
  public initialNotificationsLoading = false;
  public initialNotificationsLoaded = false;
  public hasMoreNotifications = true;
  private loadingNextNotifications = false;
  private notificationsUserId: string | null = null;
  private lastActiveTopbarItem: string = null;

  constructor(
    public appMain: SiteLayoutComponent,
    public app: AppComponent,
    private ts: TokenDataProviderService,
    private translate: TranslateService,
    private readonly router: Router,
    private cdr: ChangeDetectorRef,
    public readonly notificationDataProvider: NotificationDataProviderService,
    private readonly chatDPS: ChatDataProviderService,
    private readonly eventInvitationRS: EventInvitationRestService,
    private readonly ownerInvitationRS: OwnerInvitationRestService,
    private readonly eventParticipationRequestRS: EventParticipationRequestRestService,
    private readonly rns: ResponseNotificationService,
    private readonly tutorialOrchestrator: TutorialOrchestratorService,
    private readonly zone: NgZone,
  ) {
    super();
    afterNextRender(() => this.initResponsiveNavigation());
  }


  ngOnInit(): void {
    this.translateMenuItems();
    this.subscriptions.push(this.translate.onLangChange.subscribe((e) => {
      this.translateMenuItems();
    }));
    this.subscriptions.push(this.chatDPS.unreadCount$.subscribe((count) => {
      this.unreadChatCount = count;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }));
    this.subscriptions.push(this.notificationDataProvider.unreadCount$.subscribe((count) => {
      this.unreadNotificationCount = count;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }));
    this.subscriptions.push(this.notificationDataProvider.sessionReset$.subscribe(() => this.resetNotifications()));
    this.observeNotifications();
    this.maybeLoadNotifications();
  }

  ngOnDestroy(): void {
    this.navigationDestroyed = true;
    this.navigationResizeObserver?.disconnect();
    if (this.navigationInitialized) {
      window.removeEventListener('resize', this.onNavigationResize);
      if (this.navigationFrameId !== null) {
        cancelAnimationFrame(this.navigationFrameId);
      }
    }
    super.ngOnDestroy();
  }

  private initResponsiveNavigation(): void {
    if (this.navigationDestroyed) return;
    const slot = this.navigationSlot?.nativeElement;
    const fullMenu = this.fullNavigationElement?.nativeElement.querySelector<HTMLElement>('.p-megamenu');
    if (!slot || !fullMenu) return;
    this.navigationInitialized = true;
    this.desktopNavigationQuery = window.matchMedia('(min-width: 992px)');
    this.zone.runOutsideAngular(() => {
      if (typeof ResizeObserver !== 'undefined') {
        this.navigationResizeObserver = new ResizeObserver(this.onNavigationResize);
        this.navigationResizeObserver.observe(slot);
        this.navigationResizeObserver.observe(fullMenu);
      }
      window.addEventListener('resize', this.onNavigationResize, {passive: true});
      this.scheduleNavigationMeasurement();
    });
  }

  private scheduleNavigationMeasurement(): void {
    if (!this.navigationInitialized || this.navigationDestroyed || this.navigationFrameId !== null) return;
    this.zone.runOutsideAngular(() => {
      this.navigationFrameId = requestAnimationFrame(() => {
        this.navigationFrameId = null;
        if (!this.navigationDestroyed) this.updateResponsiveNavigation();
      });
    });
  }

  private updateResponsiveNavigation(): void {
    const slot = this.navigationSlot?.nativeElement;
    const fullElement = this.fullNavigationElement?.nativeElement;
    const fullMenu = fullElement?.querySelector<HTMLElement>('.p-megamenu');
    if (!slot || !fullMenu) return;
    const desktop = this.desktopNavigationQuery.matches;
    let requiredWidth = fullMenu.getBoundingClientRect().width;
    let showIcons = false;
    if (desktop) {
      // Measure both variants before painting: labels take priority over icons.
      const iconClass = 'topbar-navigation__full--icons';
      fullElement.classList.add(iconClass);
      const widthWithIcons = fullMenu.getBoundingClientRect().width;
      fullElement.classList.remove(iconClass);
      requiredWidth = fullMenu.getBoundingClientRect().width;
      fullElement.classList.toggle(iconClass, this.showNavigationIcons);
      showIcons = widthWithIcons > 0 && Math.ceil(widthWithIcons) <= slot.clientWidth;
    }
    const compact = desktop && (!requiredWidth || Math.ceil(requiredWidth) > slot.clientWidth);
    const navigationChanged = compact !== this.compactNavigation || desktop !== this.desktopNavigation;
    if (!navigationChanged && showIcons === this.showNavigationIcons) return;

    const previousMenu = this.desktopNavigation && this.compactNavigation ? this.compactMenu : this.fullNavigation;
    const restoreFocus = navigationChanged && previousMenu?.el.nativeElement.contains(slot.ownerDocument.activeElement);
    if (navigationChanged) {
      this.fullNavigation?.hide();
      this.compactMenu?.hide();
    }
    this.zone.run(() => {
      this.desktopNavigation = desktop;
      this.compactNavigation = compact;
      this.showNavigationIcons = showIcons;
      this.cdr.detectChanges();
      if (restoreFocus) {
        const menu = compact ? this.compactMenu : this.fullNavigation;
        const root = (menu?.el.nativeElement as HTMLElement)?.querySelector<HTMLElement>('[role="menubar"]');
        // The legacy mobile menu may be closed when crossing its breakpoint.
        const target = root?.getClientRects().length ? root
          : slot.closest('.layout-topbar')?.querySelector<HTMLElement>('.layout-topbar-logo');
        target?.focus({preventScroll: true});
      }
    });
  }

  public async startCurrentViewTutorial(event: Event): Promise<void> {
    event.preventDefault();
    this.appMain.resetActiveMenus();

    const started = await this.tutorialOrchestrator.startMatchingCampaignForCurrentRoute(true);
    if (!started) {
      this.rns.emitWarning(this.translate.instant('tutorials.settings.actions.unavailable'));
    }
  }

  private resetNotifications(): void {
    this.notificationsUserId = null;
    this.notifications = [];
    this.currentPage = 1;
    this.initialNotificationsLoading = false;
    this.initialNotificationsLoaded = false;
    this.hasMoreNotifications = true;
    this.loadingNextNotifications = false;
    this.lastActiveTopbarItem = null;
  }

  private maybeLoadNotifications(): void {
    const userId = this.user?.id ? String(this.user.id) : null;
    if (this.notificationsUserId === userId) return;
    this.resetNotifications();
    if (!userId || !this.ts.isLoggedIn()) return;
    this.notificationsUserId = userId;
    this.loadFirstPageNotifications();
  }

  private observeNotifications(): void {
    this.subscriptions.push(this.notificationDataProvider.events$.subscribe((es: EventSourceInterface<NotificationInterface>) => {
      const notification = es.payload;
      switch (es.state) {
        case 'persisted':
          this.notifications = this.limitNotificationsWindow(this.mergeNotifications([notification], this.notifications));
          break;
        case 'removed':
          this.notifications = this.notifications.filter(e => e['id'] !== notification['id']);
          break;
      }

      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }));
    this.subscriptions.push(this.appMain.onClickTopBarItem$.subscribe(() => {
      const currentActiveItem = this.appMain.activeTopbarItem || null;
      const notificationsJustClosed = this.lastActiveTopbarItem === 'notifications' && currentActiveItem !== 'notifications';
      this.lastActiveTopbarItem = currentActiveItem;

      const loadedNotifications = this.displayedNotifications.filter((notification) => !!notification?.id);
      if (notificationsJustClosed && this.unreadNotificationCount > 0 && loadedNotifications.length) {
        const loadedIds = loadedNotifications.map((notification) => notification.id);
        this.subscriptions.push(this.notificationDataProvider.massUpdateReadAt(loadedNotifications)
          .subscribe(() => {
            this.markNotificationsAsRead(loadedIds);
            this.subscriptions.push(this.notificationDataProvider.getUnreadCount().subscribe());
            this.cdr.detectChanges();
          }));
      }
    }));
  }

  activeItem: number;
  query: string;

  private _model: MegaMenuItem[] = this.mainNavigationItems
    .map((item) => ({
      label: null,
      icon: item.icon,
      state: {transKey: item.labelKey},
      routerLink: item.routerLink,
      command: () => {
        this.appMain.mobileTopbarActive = !this.appMain.mobileTopbarActive;
      },
    }));

  @ViewChild('searchInput') searchInputViewChild: ElementRef;
  @ViewChild('notificationsPanel') notificationsPanel?: ElementRef<HTMLElement>;

  onSearchAnimationEnd(event: AnimationEvent) {
    switch (event.toState) {
      case 'visible':
        this.query = '';
        this.searchInputViewChild.nativeElement.focus();
        break;
    }
  }

  private translateMenuItems(): void {
    const navigationLabel = 'shared.top_bar.menu.navigation';
    const labels = [...this._model.map(e => e.state.transKey), navigationLabel];
    this.subscriptions.push(this.translate.get(labels).pipe(tap((res) => {
      this.model = this.model.map((item: MegaMenuItem) => {
        if (res.hasOwnProperty(item.state.transKey)) {
          item.label = res[item.state.transKey];
        }
        return item;
      });
      const items: MenuItem[] = this.model.map((item) => ({
        label: item.label,
        icon: item.icon,
        routerLink: item.routerLink,
        command: item.command,
      }));
      const split = Math.ceil(items.length / 2);
      this.compactNavigationModel = [{
        label: res[navigationLabel],
        icon: 'pi pi-bars',
        items: [
          [{items: items.slice(0, split)}],
          [{items: items.slice(split)}],
        ],
      }];
      this.scheduleNavigationMeasurement();
    })).subscribe());
  }

  public logout(event: Event): void {
    this.ts.logout();
  }

  public handleNotificationItemClick(event): void {
    this.appMain.onTopbarItemClick(event, 'notifications');
    if (!this.initialNotificationsLoaded && !this.initialNotificationsLoading) {
      this.loadFirstPageNotifications();
    }
    this.cdr.detectChanges();
  }

  public handleMobileNotificationClick(event): void {
    this.appMain.onTopbarItemClick(event, 'notifications');
    if (!this.initialNotificationsLoaded && !this.initialNotificationsLoading) {
      this.loadFirstPageNotifications();
    }
    if (this.appMain.activeTopbarItem === 'notifications') {
      this.appMain.mobileTopbarActive = true;
      this.appMain.mobileTopbarItemClick = true;
    } else {
      this.appMain.mobileTopbarActive = false;
    }
    this.cdr.detectChanges();
  }

  public handleMobileChatClick(event): void {
    event.preventDefault();
    event.stopPropagation();
    this.appMain.activeTopbarItem = null;
    this.appMain.mobileTopbarActive = false;
    this.appMain.mobileTopbarItemClick = false;
    this.appMain.enableBodyScroll();
    this.router.navigate(['/chat']);
  }

  public loadNextNotificationsChunk(event: { first?: number; rows?: number }) {
    if (!this.user || !this.appMain.isLoggedIn) {
      return;
    }
    const rows = event.rows || this.pageRows;
    const first = event.first || 0;
    const page = Math.floor(first / rows) + 1;
    if (page <= this.currentPage || this.loadingNextNotifications || !this.hasMoreNotifications) {
      return;
    }
    this.loadingNextNotifications = true;
    this.subscriptions.push(this.notificationDataProvider.getCollection(page, this.pageRows).subscribe({
      next: (notifications: ResultListApiModel<NotificationInterface>) => {
        this._notifications = this.limitNotificationsWindow(this.mergeNotifications(this._notifications, notifications.records || []));
        this.currentPage = page;
        this.hasMoreNotifications = (notifications.records || []).length >= this.pageRows;
        this.tryLoadNextIfPanelNotScrollable();
      },
      complete: () => {
        this.loadingNextNotifications = false;
      },
      error: () => {
        this.loadingNextNotifications = false;
      }
    }));
  }

  public onNotificationsScroll(event: Event): void {
    if (!this.user || !this.appMain.isLoggedIn || this.loadingNextNotifications) {
      return;
    }
    const target = event.target as HTMLElement;
    if (!target) {
      return;
    }
    const threshold = 120;
    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (distanceToBottom > threshold) {
      return;
    }
    const nextPage = this.currentPage + 1;
    this.loadNextNotificationsChunk({first: (nextPage - 1) * this.pageRows, rows: this.pageRows});
  }

  public onNotificationItemInteracted(event: Event): void {
    this.appMain.onTopbarItemClick(event, 'notifications');
  }

  public removeResolvedNotification(id: number): void {
    this.notifications = this.notifications.filter(notification => notification.id !== id);
  }

  public goToSearchResults(): void {
    this.router.navigate(['/search-results'], {queryParams: {q: this.query || ''}});
  }

  public navigateToUserProfile(event: Event, notification: NotificationInterface): void {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate(['/', notification.fromUser.slug]).then(() => this.appMain.onTopbarItemClick(event, 'notifications'));
  }

  public navigateToProject(event: Event,  notification: NotificationInterface): void {
    event.preventDefault();
    event.stopPropagation();
    const route = this.buildProjectRoute(notification);
    if (!route) {
      return;
    }

    this.router.navigate(route).then(() => this.appMain.onTopbarItemClick(event, 'notifications'));
  }

  public navigateToCollaboration(event: Event, notification: NotificationInterface): void {
    event.preventDefault();
    event.stopPropagation();

    const context: any = notification?.context || {};
    const collaborationSlug = context.collaborationSlug;
    const collaborationId = this.resolveNumericId(context.collaborationId);
    const collaborationKey = collaborationSlug || collaborationId;
    const ownerSlug = context.ownerSlug;
    if (!collaborationKey || !ownerSlug) {
      return;
    }

    this.router.navigate(['/', ownerSlug, 'collaborations', collaborationKey]).then(() => this.appMain.onTopbarItemClick(event, 'notifications'));
  }

  public navigateToConversation(event: Event, notification: NotificationInterface): void {
    event.preventDefault();
    event.stopPropagation();
    const context: any = notification?.context || {};
    const conversationId = context.conversationId;
    if (!conversationId) {
      return;
    }
    this.router.navigate(['/chat', conversationId]).then(() => this.appMain.onTopbarItemClick(event, 'notifications'));
  }

  public navigateToMarketplaceOffer(event: Event, notification: NotificationInterface): void {
    event.preventDefault();
    event.stopPropagation();

    const context: any = notification?.context || {};
    const advertisementSlug = context.advertisementSlug || notification?.subject?.slug;
    if (!advertisementSlug) {
      return;
    }

    this.router.navigate(['/marketplace/offer', advertisementSlug]).then(() => this.appMain.onTopbarItemClick(event, 'notifications'));
  }

  public getNotificationProjectLabel(notification: NotificationInterface): string {
    const subject: any = notification?.subject || {};
    const context: any = notification?.context || {};
    if (context.projectName) {
      return context.projectName;
    }
    if (subject?.alias && subject?.vehicle?.fullVehicleIdentity) {
      return `${subject.alias} (${subject.vehicle.fullVehicleIdentity})`;
    }
    if (subject?.vehicle?.fullVehicleIdentity) {
      return subject.vehicle.fullVehicleIdentity;
    }
    if (subject?.alias) {
      return subject.alias;
    }
    if (notification?.context?.['projectSlug']) {
      return notification.context['projectSlug'];
    }

    return this.translate.instant('shared.top_bar.notifications.project_fallback');
  }

  public getNotificationThumbnail(notification: NotificationInterface): any {
    const subject: any = notification?.subject || {};
    if (notification?.type === 'PROJECT') {
      return subject?.thumbnail || subject?.theme || notification?.fromUser?.avatar || null;
    }

    return notification?.fromUser?.avatar || null;
  }

  public getNotificationThumbnailOptions(notification: NotificationInterface): MediaObjectOptionsInterface {
    const subject: any = notification?.subject || {};
    if (notification?.type === 'PROJECT') {
      return {
        defaultImageType: subject?.vehicleType?.includes?.('MOTORCYCLE') ? 'motorcycle' : 'car',
        formatType: 'thumb_sm'
      };
    }

    return {
      defaultImageType: 'avatar',
      formatType: 'thumb_sm'
    };
  }

  private buildProjectRoute(notification: NotificationInterface): string[] | null {
    const context: any = notification?.context || {};
    const subject: any = notification?.subject || {};
    const ownerSlug = context.ownerSlug || subject?.user?.slug || notification?.fromUser?.slug;
    const projectSlug = context.projectSlug || subject?.slug;
    if (!projectSlug) {
      return null;
    }

    return ownerSlug ? ['/', ownerSlug, 'garage', projectSlug] : ['/garage', projectSlug];
  }

  private resolveNumericId(value: any): number | null {
    if (value === undefined || value === null) {
      return null;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const plain = Number(value);
      if (Number.isFinite(plain)) {
        return plain;
      }

      const match = value.match(/(\d+)(?!.*\d)/);
      if (match) {
        return Number(match[1]);
      }
    }

    return null;
  }

  private getParticipationRequestId(notification: NotificationInterface): number | null {
    const fromContext = (notification?.context as any)?.participationRequestId;
    const fromSubject = (notification?.subject as any)?.id;
    return this.resolveNumericId(fromContext ?? fromSubject);
  }

  public navigateToInvitationPage(event: Event,  notification: NotificationInterface): void {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate(['/', 'invitation', notification.context['token']]).then(() => this.appMain.onTopbarItemClick(event, 'notifications'));
  }

  public navigateToChangeOwnerConfirmationPage(event: Event,  notification: NotificationInterface): void {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate(['/', 'owner-change-confirmation', notification.context['token']]).then(() => this.appMain.onTopbarItemClick(event, 'notifications'));
  }

  public denyOwnerInvitation(event: Event, notification: NotificationInterface): void {
    event.preventDefault();
    event.stopPropagation();
    const invitationId = notification?.context?.['invitationId'];
    if (!invitationId) {
      return;
    }
    const invitation = new OwnerInvitationApiModel();
    invitation.id = invitationId;
    this.ownerInvitationRS.deny(invitation).subscribe(() => {
      this.appMain.onTopbarItemClick(event, 'notifications');
    });
  }

  public navigateToEvent(event: Event, notification: NotificationInterface): void {
    event.preventDefault();
    event.stopPropagation();
    if (!notification?.context?.['eventSlug']) {
      return;
    }
    this.router.navigate(['/', 'events', notification.context['eventSlug']]).then(() => this.appMain.onTopbarItemClick(event, 'notifications'));
  }

  public acceptEventInvitation(event: Event, notification: NotificationInterface): void {
    event.preventDefault();
    event.stopPropagation();
    const invitationId = notification?.context?.['invitationId'];
    if (!invitationId) {
      return;
    }
    const invitation = new EventInvitationApiModel();
    invitation.id = invitationId;
    this.eventInvitationRS.accept(invitation).subscribe(() => {
      this.appMain.onTopbarItemClick(event, 'notifications');
    });
  }

  public denyEventInvitation(event: Event, notification: NotificationInterface): void {
    event.preventDefault();
    event.stopPropagation();
    const invitationId = notification?.context?.['invitationId'];
    if (!invitationId) {
      return;
    }
    const invitation = new EventInvitationApiModel();
    invitation.id = invitationId;
    this.eventInvitationRS.deny(invitation).subscribe(() => {
      this.appMain.onTopbarItemClick(event, 'notifications');
    });
  }

  public approveEventParticipationRequest(event: Event, notification: NotificationInterface): void {
    event.preventDefault();
    event.stopPropagation();

    const requestId = this.getParticipationRequestId(notification);
    if (!requestId) {
      return;
    }

    this.eventParticipationRequestRS.approve(requestId).subscribe({
      next: () => {
        this.markParticipationNotificationResolved(notification, 'APPROVED');
        this.appMain.onTopbarItemClick(event, 'notifications');
      },
      error: (errorResponse: HttpErrorResponse) => this.handleParticipationRequestError(errorResponse)
    });
  }

  public rejectEventParticipationRequest(event: Event, notification: NotificationInterface): void {
    event.preventDefault();
    event.stopPropagation();

    const requestId = this.getParticipationRequestId(notification);
    if (!requestId) {
      return;
    }

    this.eventParticipationRequestRS.reject(requestId).subscribe({
      next: () => {
        this.markParticipationNotificationResolved(notification, 'REJECTED');
        this.appMain.onTopbarItemClick(event, 'notifications');
      },
      error: (errorResponse: HttpErrorResponse) => this.handleParticipationRequestError(errorResponse)
    });
  }

  private markParticipationNotificationResolved(
    notification: NotificationInterface,
    status: 'APPROVED' | 'REJECTED'
  ): void {
    if (!notification) {
      return;
    }

    notification.subject = {
      ...(notification.subject || {}),
      status
    };
  }

  getNotificationParams(notification: NotificationInterface): object {
    return {
      mainOwnerUsername: notification.fromUser.username,
      user: notification.fromUser.username,
      vehicleName: notification.context?.['vehicleName'],
      advertisementTitle: notification.context?.['advertisementTitle'] || notification.subject?.title,
      invitedByUsername: notification.fromUser.username,
      ...(notification.context || {})
    };
  }

  public getMarketplaceChangedFieldsLabels(notification: NotificationInterface): string {
    const context: any = notification?.context || {};
    const changedFields: string[] = Array.isArray(context.changedFields) ? context.changedFields : [];

    return changedFields
      .map((field) => this.translate.instant(`shared.top_bar.notifications.marketplace_followed_offer_updated.field.${field}`))
      .filter((label) => !!label)
      .join(', ');
  }

  public getParticipationVehiclesLabel(notification: NotificationInterface): string {
    const vehicles: Array<{projectIdentity?: string; projectSlug?: string}> = (notification?.context as any)?.vehicles || [];
    return vehicles
      .map((vehicle) => vehicle?.projectIdentity || vehicle?.projectSlug || '')
      .filter((label) => !!label)
      .join(', ');
  }

  private handleParticipationRequestError(errorResponse: HttpErrorResponse): void {
    const description = errorResponse?.error?.['hydra:description'];
    if (description === 'Only pending requests can be approved.' || description === 'Only pending requests can be rejected.') {
      this.rns.emitWarning(this.translate.instant('features.event.event_show.participation_request.not_pending'));
      return;
    }

    this.rns.emitError(this.translate.instant('features.event.event_show.participation_request.action_error'));
  }

  get displayedNotifications(): NotificationInterface[] {
    return this.notifications;
  }

  get displayedNotificationItems(): (NotificationInterface & {isPlaceholder?: boolean})[] {
    if (this.initialNotificationsLoading && !this.displayedNotifications.length) {
      return this.buildNotificationPlaceholders(this.getNotificationPlaceholderCount());
    }
    return this.displayedNotifications;
  }

  get isLoadingNextNotifications(): boolean {
    return this.loadingNextNotifications;
  }

  get showEndOfNotifications(): boolean {
    return this.initialNotificationsLoaded && !this.initialNotificationsLoading && this.displayedNotifications.length > 0 && !this.hasMoreNotifications && !this.loadingNextNotifications;
  }

  private sortNotifications(notifications: NotificationInterface[]): NotificationInterface[] {
    return [...notifications].sort((a, b) => {
      const aCreatedAt = a?.createdAt ? new Date(a.createdAt as any).getTime() : 0;
      const bCreatedAt = b?.createdAt ? new Date(b.createdAt as any).getTime() : 0;
      if (aCreatedAt === bCreatedAt) {
        return (b?.id || 0) - (a?.id || 0);
      }
      return bCreatedAt - aCreatedAt;
    });
  }

  private mergeNotifications(existing: NotificationInterface[], incoming: NotificationInterface[]): NotificationInterface[] {
    const map = new Map<number, NotificationInterface>();
    [...existing, ...incoming].forEach((notification) => {
      if (!notification?.id) {
        return;
      }
      map.set(notification.id, notification);
    });

    return this.sortNotifications(Array.from(map.values()));
  }

  private applyFirstPageNotifications(result: ResultListApiModel<NotificationInterface>): void {
    const fetched = this.sortNotifications(result?.records || []);
    const hasExisting = this.notifications.length > 0;
    const total = result?.total || 0;

    if (fetched.length || !hasExisting || total === 0) {
      this.notifications = this.limitNotificationsWindow(fetched);
    }
  }

  private loadFirstPageNotifications(): void {
    const userId = this.notificationsUserId;
    this.initialNotificationsLoading = true;
    this.subscriptions.push(this.notificationDataProvider.getCollection(1, this.pageRows).subscribe({
      next: (notifications: ResultListApiModel<NotificationInterface>) => {
        if (this.notificationsUserId !== userId) return;
        this.applyFirstPageNotifications(notifications);
        this.currentPage = 1;
        this.hasMoreNotifications = (notifications.records || []).length >= this.pageRows;
        this.tryLoadNextIfPanelNotScrollable();
      },
      complete: () => {
        if (this.notificationsUserId !== userId) return;
        this.initialNotificationsLoading = false;
        this.initialNotificationsLoaded = true;
      },
      error: () => {
        if (this.notificationsUserId !== userId) return;
        this.initialNotificationsLoading = false;
      }
    }));
  }

  private limitNotificationsWindow(notifications: NotificationInterface[]): NotificationInterface[] {
    if (notifications.length <= this.maxRenderedNotifications) {
      return notifications;
    }
    return notifications.slice(0, this.maxRenderedNotifications);
  }

  private markNotificationsAsRead(ids: number[]): void {
    if (!ids?.length) {
      return;
    }
    const now = new Date();
    const idsSet = new Set(ids);
    this.notifications = this.notifications.map((notification) => {
      if (!idsSet.has(notification.id)) {
        return notification;
      }
      return {...notification, readAt: notification.readAt || now} as NotificationInterface;
    });
  }

  private tryLoadNextIfPanelNotScrollable(): void {
    if (!this.hasMoreNotifications || this.loadingNextNotifications) {
      return;
    }
    setTimeout(() => {
      const panel = this.notificationsPanel?.nativeElement;
      if (!panel || panel.scrollHeight > panel.clientHeight + 1) {
        return;
      }
      const nextPage = this.currentPage + 1;
      this.loadNextNotificationsChunk({first: (nextPage - 1) * this.pageRows, rows: this.pageRows});
    });
  }

  private createNotificationPlaceholder(index: number): NotificationInterface & {isPlaceholder: true} {
    return {
      id: -(index + 1),
      fromUser: {username: '', slug: ''},
      subject: {},
      event: 'project_follow',
      type: '',
      context: {},
      createdAt: null as any,
      readAt: null as any,
      clickedAt: null as any,
      isPlaceholder: true
    };
  }

  private buildNotificationPlaceholders(count: number): (NotificationInterface & {isPlaceholder: true})[] {
    return Array.from({length: count}, (_, index) => this.createNotificationPlaceholder(index));
  }

  private getNotificationPlaceholderCount(): number {
    const panelElement = this.notificationsPanel?.nativeElement;
    const panelHeight = panelElement?.clientHeight || 0;
    if (panelHeight <= 0) {
      return Math.max(8, Math.ceil((window?.innerHeight || 800) / this.notificationPlaceholderRowHeight));
    }

    return Math.max(6, Math.floor(panelHeight / this.notificationPlaceholderRowHeight));
  }
}
